import { CommonModule, DatePipe } from '@angular/common';
import { ChangeDetectorRef, Component, OnInit, OnDestroy, inject, HostListener, ElementRef } from '@angular/core';
import { NavbarComponent } from "../../shared/components/navbar/navbar.component";
import { ActivatedRoute, Router } from '@angular/router';
import { packages } from 'src/app/core/models/packages';
import { NgxJsonViewerModule } from 'ngx-json-viewer';
import { ApiService } from 'src/app/core/services/api.service';
import { PdfService } from 'src/app/core/services/pdf.service';
import { Observable, combineLatestWith, take, BehaviorSubject, of, map } from 'rxjs';
import { MatButtonModule, MatIconButton } from '@angular/material/button';
import { MatDialogModule } from '@angular/material/dialog';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTabsModule } from '@angular/material/tabs';
import { MyAuthService } from 'src/app/core/services/auth.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { FormsModule } from '@angular/forms';
import { AuthService } from '@auth0/auth0-angular';
import { FooterComponent } from "../../shared/components/footer/footer.component";
import { environment } from 'src/environments/environment';
import { AdminInspectService } from 'src/app/core/services/inspect.service';
import { DisclaimerComponent } from 'src/app/shared/components/diligence/disclaimer/disclaimer.component';
import { ModifyDiligenceComponent } from 'src/app/shared/components/diligence/modify-diligence/modify-diligence.component';
import { ReportSection, buildReportSections } from 'src/app/shared/components/diligence/report-sections';
import { PdfHeaderComponent } from 'src/app/shared/components/diligence/pdf-header/pdf-header.component';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ReportSpec, TabSpec, SectionSpec } from 'src/app/core/interfaces/report-spec.interface';
import { UniversalReportRendererComponent } from 'src/app/shared/components/universal-report-renderer/universal-report-renderer.component';
import { SectionEditorComponent } from '../admin/spec-editor/section-editor.component';
import { isReportReady } from 'src/app/core/models/report-readiness';
import { normalizeR8Report, R8ReportModel } from 'src/app/core/models/r8-report.model';
import { R8PdfRendererService } from 'src/app/core/services/r8-pdf-renderer.service';
import { NavigationRailComponent } from 'src/app/shared/components/navigation-rail/navigation-rail.component';
import { AviReportComponent } from '../avi-report/avi-report.component';

@Component({
  selector: 'app-diligence',
  templateUrl: './diligence.component.html',
  styleUrls: ['./diligence.component.scss'],
  standalone: true,
  providers: [ApiService, DatePipe, MatSnackBar, PdfService],
  imports: [
    CommonModule,
    ModifyDiligenceComponent,
    MatIconModule,
    PdfHeaderComponent,
    DisclaimerComponent,
    MatInputModule,
    MatProgressSpinnerModule,
    MatDialogModule,
    NgxJsonViewerModule,
    NavbarComponent,
    NavigationRailComponent,
    MatTabsModule,
    MatButtonModule,
    MatIconButton,
    FormsModule,
    FooterComponent,
    MatTooltipModule,
    UniversalReportRendererComponent,
    SectionEditorComponent,
    AviReportComponent
  ]
})
export class DiligenceComponent implements OnInit, OnDestroy {
  private pdfViewSubject = new BehaviorSubject<boolean>(false);
  pdfView$ = this.pdfViewSubject.asObservable();
  showInspect$: Observable<boolean>;
  sections$: Observable<ReportSection[]> = of([]);
  visibleSections$: Observable<ReportSection[]> = of([]);
  printableSections$: Observable<ReportSection[]> = of([]);
  packages = packages;
  packageName = '';

  // Inject services
  private inspectService = inject(AdminInspectService);
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private apiService = inject(ApiService);
  private datePipe = inject(DatePipe);
  private myAuthService = inject(MyAuthService);
  private snackBar = inject(MatSnackBar);
  private auth = inject(AuthService);
  private cdr = inject(ChangeDetectorRef);

  currentReportSpec?: ReportSpec;

  constructor(
    private elementRef: ElementRef,
    inspectService: AdminInspectService,
    private pdfService: PdfService,
    private r8PdfRenderer: R8PdfRendererService
  ) {
    this.showInspect$ = this.inspectService.showInspect$;
  }

  // Inspector resize state
  inspectorWidth = Math.floor(window.innerWidth * 0.5);
  isResizing = false;
  startX = 0;
  startWidth = 0;

  // Component state
  diligence: any;
  error = '';
  transformedData: any;
  originalDiligence: any;
  isAdmin$: Observable<boolean> = this.myAuthService.isAdmin();
  loading = false;
  environment = environment.environment;
  isReportVoided = false;
  isR8Report = false;
  isAviReport = false;
  showR8AdminControls = false;
  r8Report: R8ReportModel = normalizeR8Report({});

  isReportReady(report: any): boolean { return isReportReady(report); }

  // Spec editor state
  selectedEditorTabIndex = 0;
  specDirty = false;
  showJsonView = false;
  savingSpec = false;
  expandedSectionIndex: number | null = 0;

  get currentEditorTab(): TabSpec | null {
    return this.currentReportSpec?.tabs[this.selectedEditorTabIndex] || null;
  }

  ngOnInit(): void {
    this.route.params.subscribe(params => {
      this.openDiligenceById(params['id']);
    });
  }

  startResize(event: MouseEvent) {
    this.isResizing = true;
    this.startX = event.clientX;
    this.startWidth = this.inspectorWidth;
    event.preventDefault();
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  }

  @HostListener('window:mousemove', ['$event'])
  onMouseMove(event: MouseEvent) {
    if (this.isResizing) {
      event.preventDefault();
      const deltaX = this.startX - event.clientX;
      const newWidth = this.startWidth + deltaX;
      const minWidth = 300;
      const maxWidth = Math.floor(window.innerWidth * 0.7);
      this.inspectorWidth = Math.max(minWidth, Math.min(newWidth, maxWidth));
      this.cdr.detectChanges();
    }
  }

  @HostListener('window:mouseup', ['$event'])
  onMouseUp(event: MouseEvent) {
    if (this.isResizing) {
      this.isResizing = false;
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    }
  }

  toggleInspect() {
    this.inspectService.toggleInspect();
  }

  downloadJson() {
    if (!this.diligence) {
      this.snackBar.open('No data available to download', 'Close', { duration: 3000 });
      return;
    }

    const dataStr = JSON.stringify(this.diligence, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = window.URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;

    const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
    const companyName = this.diligence?.companyDetails?.companyName || 'diligence';
    link.download = `${companyName}_${timestamp}.json`;

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);

    this.snackBar.open('JSON file downloaded successfully', 'Close', { duration: 3000 });
  }

  mapScoreElement(arr: any, type: string) {
    if (type === 'date') {
      return arr.map((obj: { date: any }) => this.datePipe.transform(obj.date, 'yyyy-MM')).reverse();
    }
    if (type === 'industryValue') {
      return arr.map((obj: { industryValue: any }) => obj.industryValue).reverse();
    }
    if (type === 'companyValue') {
      return arr.map((obj: { companyValue: any }) => Number(obj.companyValue)).reverse();
    }
  }

  updateDiligence() {
    this.apiService.putDiligence({
      phase: Number(this.diligence.phase),
      repScore: Number(this.diligence.repScore),
      repSumm: this.diligence.repSumm,
      sancIssueSummary: this.diligence.sancIssueSummary,
      critIssueSummary: this.diligence.critIssueSummary,
      critIssueDetails: this.diligence.critIssueDetails,
      previousDiligencePhase: this.originalDiligence.phase,
      uuid: this.diligence.uuid,
      userEmail: this.diligence.userEmail
    }).subscribe(() => {
      this.snackBar.open('diligence modified succesfully');
      this.openDiligenceById(this.diligence.uuid);
    });
  }

  openDiligenceById(id: string) {
    this.loading = true;

    this.apiService.getDiligenceById(id).pipe(
      take(1),
      combineLatestWith(this.auth.user$, this.isAdmin$, this.showInspect$)
    ).subscribe({
      next: ([diligence, user, isAdmin, showInspect]) => {
        const reportCode = diligence.dueDiligence.report;
        const rawData = diligence.dueDiligence;
        const uuid = rawData.uuid;

        // AVI has its own projection endpoint and does not depend on the
        // editor/spec contract used by legacy diligence packages.
        if (reportCode?.toUpperCase() === 'R12') {
          this.processReportWithSpec(rawData, reportCode, { metadata: {} } as ReportSpec, isAdmin, showInspect);
          this.setDiligenceData(rawData, user, isAdmin);
          this.loading = false;
          return;
        }

        // Fetch spec from backend
        this.apiService.getReportSpec(uuid).pipe(take(1)).subscribe({
          next: (response) => {
            console.log(`Fetched ${reportCode} spec from backend (v${response.version}):`, response.spec);
            this.processReportWithSpec(rawData, reportCode, response.spec, isAdmin, showInspect);
            this.setDiligenceData(rawData, user, isAdmin);
            this.loading = false;
          },
          error: (specError) => {
            console.error(`Failed to fetch spec from backend:`, specError);
            this.error = 'Failed to load report specification';
            this.loading = false;
          }
        });
      },
      error: (error) => {
        this.loading = false;
        this.error = error.error?.error || 'Failed to load report';
      }
    });
  }

  private setDiligenceData(rawData: any, user: any, isAdmin: boolean) {
    if ((rawData.userEmail === user?.email || isAdmin) && user?.email) {
      this.diligence = rawData;
      this.originalDiligence = { ...rawData };
      this.isReportVoided = rawData.credit?.status === 'voided';
    }
  }

  private processReportWithSpec(
    rawData: any,
    reportCode: string,
    spec: ReportSpec,
    isAdmin: boolean,
    showInspect: boolean
  ) {
    this.isR8Report = reportCode?.toUpperCase() === 'R8';
    this.isAviReport = reportCode?.toUpperCase() === 'R12';
    this.r8Report = this.isR8Report ? normalizeR8Report(rawData) : normalizeR8Report({});
    console.log(`${reportCode} diligence (raw)`, rawData);

    // Use spec from backend
    this.currentReportSpec = spec;

    // Build sections from spec
    this.sections$ = this.pdfView$.pipe(
      map((pdfView: boolean) => buildReportSections(rawData, pdfView, this.currentReportSpec))
    );

    this.visibleSections$ = this.sections$.pipe(
      map(sections => sections.filter(s => s.display || (isAdmin && showInspect)))
    );

    this.printableSections$ = this.sections$.pipe(
      map(sections => sections.filter(s => s.print))
    );

    // Set package name from packages list or spec metadata
    const pkg = packages.find(p => p.code === reportCode);
    this.packageName = pkg?.name || spec.metadata?.title || `${reportCode} Report`;

    // Set report data
    this.transformedData = rawData;
  }

  generatePDF(): void {
    if (this.isR8Report) {
      this.pdfService.generateR8ReportPdf(this.r8Report, this.diligence, this.r8PdfRenderer)
        .catch((error: Error) => {
          console.error('Error generating R8 PDF:', error);
          this.snackBar.open('Unable to generate the report PDF', 'Close', { duration: 4000 });
        });
      return;
    }
    this.updatePdfView(true);

    this.printableSections$.pipe(take(1)).subscribe(sections => {
      this.pdfService.generateReportPdf(sections, this.packageName, this.transformedData, this.diligence)
        .then(() => this.updatePdfView(false))
        .catch((error: Error) => {
          console.error('Error generating PDF:', error);
          this.updatePdfView(false);
        });
    });
  }

  updatePdfView(newValue: boolean) {
    this.pdfViewSubject.next(newValue);
  }

  handleVoidStatusChanged(isVoided: boolean) {
    this.isReportVoided = isVoided;
    this.openDiligenceById(this.diligence.uuid);
    this.cdr.detectChanges();
  }

  // Spec Editor Methods
  onSpecChanged(): void {
    this.specDirty = true;
    // Spec is mutated in-place and passed by reference to components
    // Just trigger change detection - no need to rebuild observables
    this.cdr.detectChanges();
  }

  saveSpec(): void {
    if (!this.currentReportSpec || !this.diligence?.uuid) return;

    this.savingSpec = true;
    this.apiService.updateReportSpec(this.diligence.uuid, this.currentReportSpec).subscribe({
      next: () => {
        this.savingSpec = false;
        this.specDirty = false;
        this.snackBar.open('Spec saved successfully', 'Close', { duration: 3000 });
      },
      error: (err) => {
        this.savingSpec = false;
        this.snackBar.open('Failed to save: ' + (err.error?.message || 'Unknown error'), 'Close', { duration: 5000 });
      }
    });
  }

  regenerateSpec(): void {
    if (!this.diligence?.uuid) return;

    if (!confirm('This will regenerate the spec from raw data. Any manual edits will be lost. Continue?')) {
      return;
    }

    this.savingSpec = true;
    this.apiService.regenerateReportSpec(this.diligence.uuid).subscribe({
      next: (response) => {
        this.currentReportSpec = response.spec;
        this.savingSpec = false;
        this.specDirty = false;
        this.onSpecChanged(); // Trigger re-render
        this.snackBar.open('Spec regenerated successfully', 'Close', { duration: 3000 });
      },
      error: (err) => {
        this.savingSpec = false;
        this.snackBar.open('Failed to regenerate: ' + (err.error?.message || 'Unknown error'), 'Close', { duration: 5000 });
      }
    });
  }

  addSection(type: 'text' | 'list' | 'card' | 'table'): void {
    if (!this.currentEditorTab) return;

    let section: SectionSpec;
    switch (type) {
      case 'text':
        section = { type: 'text', content: 'New text content', variant: 'body' };
        break;
      case 'list':
        section = { type: 'list', items: ['Item 1'] };
        break;
      case 'card':
        section = { type: 'card', title: 'New Card', content: [] };
        break;
      case 'table':
        section = { type: 'table', title: 'New Table', columns: ['Column 1', 'Column 2'], rows: [['', '']] };
        break;
    }

    this.currentEditorTab.sections.push(section);
    this.expandedSectionIndex = this.currentEditorTab.sections.length - 1;
    this.onSpecChanged();
  }

  removeSection(index: number): void {
    if (!this.currentEditorTab) return;
    if (confirm('Remove this section?')) {
      this.currentEditorTab.sections.splice(index, 1);
      this.onSpecChanged();
    }
  }

  moveSection(index: number, direction: number): void {
    if (!this.currentEditorTab) return;
    const newIndex = index + direction;
    if (newIndex < 0 || newIndex >= this.currentEditorTab.sections.length) return;

    const sections = this.currentEditorTab.sections;
    [sections[index], sections[newIndex]] = [sections[newIndex], sections[index]];
    this.expandedSectionIndex = newIndex;
    this.onSpecChanged();
  }

  ngOnDestroy() {
    if (this.isResizing) {
      this.isResizing = false;
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    }
  }
}
